import React, { useState, useEffect } from "react";
import {
  fetchConversations,
  togglePinConversation,
  deleteConversation,
  downloadAudioFile,
  streamAudioFile,
  Conversation,
  ConversationsResponse,
} from "../../services/api";
import StereoAudioVisualizer from "../Common/AudioVisualizer/AudioVisualizer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faPause,
  faDownload,
  faTimes,
  faStar,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import {
  HiStar,
  HiOutlineStar,
  HiTrash,
  HiDownload,
  HiPlay,
  HiPause,
} from "react-icons/hi";
import "../Common/AudioVisualizer/AudioVisualizerStyles.css";
import "../Admin/Admin.css";
import ConfirmationModal from "../Common/ConfirmationModal";
import LoadingState from "../Common/LoadingState";

// Extend the ConversationsResponse interface to include remaining_uses
interface ExtendedConversationsResponse extends ConversationsResponse {
  // remaining_uses removed
}

const Conversations: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [audioLoaded, setAudioLoaded] = useState<boolean>(false);
  const [audioLoading, setAudioLoading] = useState<boolean>(false);
  const [expandedConversation, setExpandedConversation] = useState<
    string | null
  >(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [conversationToDelete, setConversationToDelete] =
    useState<Conversation | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);

  // Reset expanded conversation when page changes
  useEffect(() => {
    setExpandedConversation(null);
  }, [currentPage]);

  // Load conversations on initial render and when page changes
  useEffect(() => {
    loadConversations();
  }, [currentPage]);

  // Clean up audio URL when component unmounts
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Clean up when modal is closed
  useEffect(() => {
    if (!showModal) {
      setIsPlaying(false);
      setAudioLoaded(false);
      setAudioError(null);
    }
  }, [showModal]);

  // Fetch conversations from API
  const loadConversations = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      const response = (await fetchConversations(
        pageSize,
        offset
      )) as ExtendedConversationsResponse;
      setConversations(response.data || []);

      if (response.pagination) {
        setTotalPages(
          Math.ceil(response.pagination.total / response.pagination.limit)
        );
      }
      setLoading(false);
    } catch (err: any) {
      setError(err.message || "Failed to load conversations");
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return `${date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    })} IST`;
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getVisiblePageItems = (): Array<number | string> => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = new Set<number>([
      1,
      totalPages,
      currentPage - 1,
      currentPage,
      currentPage + 1,
    ]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    sortedPages.forEach((page, index) => {
      const previousPage = sortedPages[index - 1];
      if (previousPage && page - previousPage > 1) {
        items.push(`dots-${previousPage}-${page}`);
      }
      items.push(page);
    });
    return items;
  };

  // Add helper function to generate consistent IDs
  const generateConversationId = (conv: Conversation): string => {
    if (conv.id) {
      return conv.id.toString();
    }
    return `${conv.lead_id}-${conv.agent_id}-${new Date(
      conv.timestamp
    ).getTime()}`;
  };

  // Update toggle function to use string IDs
  const toggleExpandConversation = (conversation: Conversation) => {
    const convId = generateConversationId(conversation);
    if (expandedConversation === convId) {
      setExpandedConversation(null);
    } else {
      setExpandedConversation(convId);
    }
  };

  // Update pin function to handle composite IDs
  const handlePinConversation = async (conversation: Conversation) => {
    try {
      const convId = generateConversationId(conversation);
      await togglePinConversation(convId);
      // Refresh the list after pinning/unpinning
      loadConversations();
    } catch (err: any) {
      setError(
        `Failed to ${conversation.pinned ? "unpin" : "pin"} conversation: ${
          err.message
        }`
      );
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (conversation: Conversation) => {
    setConversationToDelete(conversation);
    setShowDeleteModal(true);
  };

  // Update delete function to handle composite IDs
  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;

    try {
      const convId = generateConversationId(conversationToDelete);
      await deleteConversation(convId);
      loadConversations();
      setShowDeleteModal(false);
      setConversationToDelete(null);
    } catch (err: any) {
      setError(`Failed to delete conversation: ${err.message}`);
    }
  };

  // Handle play button click
  const handlePlay = async (conversation: Conversation) => {
    if (!conversation.recording_filename) {
      setError("No recording available for this conversation");
      return;
    }

    try {
      // Reset error state at the start
      setAudioError(null);

      // If we're already showing this conversation, just toggle play state
      if (
        selectedConversation?.recording_filename ===
          conversation.recording_filename &&
        audioLoaded &&
        audioUrl
      ) {
        setIsPlaying(!isPlaying);
        setShowModal(true);
        return;
      }

      // Stop any current playback
      setIsPlaying(false);

      // Set new selected recording
      setSelectedConversation(conversation);
      setShowModal(true);
      setAudioLoading(true);

      // Clean up previous URL if exists
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      // Download new audio
      const audioBlob = await streamAudioFile(
        conversation.recording_filename
      );

      if (!audioBlob) {
        throw new Error(
          "Failed to download audio file - received empty response"
        );
      }

      // Create new URL from blob
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);
      setAudioLoaded(true);
      setAudioLoading(false);

      // Short delay before playing to let audio element initialize
      setTimeout(() => {
        setIsPlaying(true);
      }, 100);
    } catch (err: any) {
      console.error("Error loading audio:", err);
      setAudioError(
        err.message ||
          "Unable to load audio file. The file may be missing or corrupted."
      );
      setAudioLoading(false);
      setAudioLoaded(false);
      setAudioUrl(null);
    }
  };

  // Handle in-modal play toggle without making API calls
  const togglePlayState = () => {
    setIsPlaying(!isPlaying);
  };

  // Handle download button click
  const handleDownload = async (conversation: Conversation) => {
    if (!conversation.recording_filename) {
      setError("No recording available for this conversation");
      return;
    }

    try {
      setError(null);
      const audioBlob = await downloadAudioFile(
        conversation.recording_filename
      );

      if (!audioBlob) {
        throw new Error(
          "Failed to download audio file - received empty response"
        );
      }

      // Create download link
      const url = URL.createObjectURL(audioBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", conversation.recording_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Clean up
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Error downloading audio:", err);
      setError(
        err.message ||
          "Unable to download audio file. The file may be missing or corrupted."
      );
    }
  };

  // Close modal and stop playback
  const closeModal = () => {
    setIsPlaying(false);
    setShowModal(false);
  };

  // Handle audio ended event
  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="min-h-screen">
        <LoadingState message="Loading conversations..." fullPage={true} />
      </div>
    );
  }

  const isAdmin = localStorage.getItem("userType") === "admin";

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[#140d0c]">Conversations</h1>
      </div>

      <div className="bg-[var(--card-container)] shadow-md rounded-lg p-6">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm mb-4">
            {error}
            <button
              className="ml-2 text-red-700 hover:text-red-900"
              onClick={() => setError(null)}
            >
              ✕
            </button>
          </div>
        )}

        {/* Global loading indicator - centered in container instead of fixed position */}
        {loading && !audioLoading && conversations.length > 0 && (
          <div className="flex justify-center items-center mb-4">
            <LoadingState
              size="small"
              padding="none"
              message="Loading more..."
              fullPage={false}
            />
          </div>
        )}

        {conversations.length === 0 && !loading ? (
          <div className="text-center py-8">
            <div className="flex flex-col items-center justify-center py-12">
              <img
                src="/assets/no-conversations.png"
                alt="No conversations"
                className="w-44 h-auto mb-6"
              />
              <h3 className="text-2xl font-bold text-[var(--foreground)] mb-4">
                No Conversations Yet
              </h3>
              <p className="text-[var(--foreground-muted)] text-lg max-w-lg mx-auto mb-8">
                Start chatting with an AI voice agent to see your conversations
                here....
              </p>
              <button
                onClick={() => (window.location.href = "/")}
                className="px-6 py-3 bg-[var(--accent)] text-white rounded-md hover:bg-[var(--accent-hover)] transition-colors font-semibold"
              >
                Start New Conversation
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {conversations.map((conversation) => (
              <div
                key={generateConversationId(conversation)}
                className={`bg-[var(--card)] rounded-lg border ${
                  conversation.pinned
                    ? "border-[var(--accent)] shadow-md"
                    : "border-[var(--border)]"
                } hover:shadow-md transition-all duration-300`}
              >
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => toggleExpandConversation(conversation)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="min-w-0 flex-1 pr-3">
                      <div className="flex items-center">
                        {conversation.pinned && isAdmin && (
                          <span
                            className="mr-2 text-[var(--accent)]"
                            title="Pinned conversation"
                          >
                            <HiStar className="h-5 w-5" />
                          </span>
                        )}
                        <h3 className="text-lg font-medium text-[var(--foreground)]">
                          {conversation.agent_name ||
                            conversation.agent?.name ||
                            `Agent #${conversation.agent_id}`}
                        </h3>
                      </div>
                      <p className="text-sm text-[var(--foreground-muted)]">
                        {formatDate(conversation.timestamp)}
                      </p>
                      {conversation.call_number && (
                        <p className="mt-1 flex flex-wrap items-baseline gap-x-1 text-xs text-[var(--foreground-muted)]">
                          <span>Phone:</span>
                          <span className="font-mono text-[var(--foreground)] break-all">
                            {conversation.call_number}
                          </span>
                        </p>
                      )}
                    </div>

                    <div className="flex shrink-0 items-center space-x-2">
                      {conversation.recording_filename && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePlay(conversation);
                            }}
                            className="text-[var(--foreground-muted)] hover:text-[var(--accent)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                            title="Play recording"
                          >
                            <HiPlay className="w-4 h-4" />
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(conversation);
                            }}
                            className="text-[var(--foreground-muted)] hover:text-[var(--accent)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                            title="Download recording"
                          >
                            <HiDownload className="w-4 h-4" />
                          </button>
                        </>
                      )}

                      {/* Only show pin/unpin and delete buttons for admin users */}
                      {isAdmin && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePinConversation(conversation);
                            }}
                            className="text-[var(--foreground-muted)] hover:text-[var(--accent)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                            title={
                              conversation.pinned
                                ? "Unpin conversation"
                                : "Pin conversation"
                            }
                          >
                            {conversation.pinned ? (
                              <HiStar className="w-4 h-4 text-[var(--accent)]" />
                            ) : (
                              <HiOutlineStar className="w-4 h-4" />
                            )}
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(conversation);
                            }}
                            className="text-[var(--foreground-muted)] hover:text-[var(--destructive)] bg-[var(--muted)] rounded-full w-8 h-8 flex items-center justify-center hover:bg-[var(--background)] transition-colors border border-[var(--border)]"
                            title="Delete conversation"
                          >
                            <HiTrash className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {expandedConversation ===
                  generateConversationId(conversation) &&
                  conversation.chat_history && (
                    <div className="border-t border-[var(--border)] p-4">
                      <div className="max-h-80 overflow-y-auto space-y-3">
                        {conversation.chat_history.map((message, index) => (
                          <div
                            key={index}
                            className={`chat-message ${
                              message.role === "assistant"
                                ? "assistant"
                                : "user"
                            }`}
                          >
                            <p className="text-sm">{message.content}</p>
                            <p className="message-sender">
                              {message.role === "assistant" ? "Agent" : "User"}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mx-auto mt-6 w-4/5 max-w-full overflow-hidden">
            <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
              <button
                className="shrink-0 px-3 py-1 bg-[var(--muted)] text-[var(--foreground)] rounded hover:bg-[var(--muted-foreground)] transition-colors disabled:opacity-50"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              {getVisiblePageItems().map((item) =>
                typeof item === "number" ? (
                  <button
                    key={item}
                    className={`shrink-0 min-w-8 px-3 py-1 rounded ${
                      item === currentPage
                        ? "bg-[var(--accent)] text-[var(--accent-foreground)]"
                        : "bg-[var(--muted)] text-[var(--foreground)] hover:bg-[var(--muted-foreground)]"
                    } transition-colors`}
                    onClick={() => handlePageChange(item)}
                    aria-current={item === currentPage ? "page" : undefined}
                  >
                    {item}
                  </button>
                ) : (
                  <span
                    key={item}
                    className="shrink-0 min-w-5 px-1 text-center text-[var(--foreground-muted)]"
                    aria-hidden="true"
                  >
                    .
                  </span>
                )
              )}

              <button
                className="shrink-0 px-3 py-1 bg-[var(--muted)] text-[var(--foreground)] rounded hover:bg-[var(--muted-foreground)] transition-colors disabled:opacity-50"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal with audio visualizer */}
      {showModal && selectedConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--card-container)] rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="p-4 border-b border-[var(--border)] flex justify-between items-center">
              <h2 className="text-lg font-medium text-[var(--foreground)]">
                {selectedConversation.agent_name ||
                  selectedConversation.agent?.name ||
                  `Agent #${selectedConversation.agent_id}`}
                <span className="text-sm text-[var(--foreground-muted)] ml-2">
                  {formatDate(selectedConversation.timestamp)}
                </span>
              </h2>
              <button
                className="text-[var(--foreground-muted)] hover:text-[var(--foreground)]"
                onClick={closeModal}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-6 bg-[var(--card-container)] rounded-b-lg">
              {audioLoading ? (
                <div className="flex items-center justify-center py-10">
                  <LoadingState
                    size="medium"
                    padding="none"
                    message="Loading audio..."
                    fullPage={false}
                  />
                </div>
              ) : audioError ? (
                <div className="text-center py-8">
                  <p className="text-[var(--destructive)]">{audioError}</p>
                </div>
              ) : (
                audioUrl && (
                  <StereoAudioVisualizer
                    audioUrl={audioUrl}
                    isPlaying={isPlaying}
                    onEnded={handleAudioEnded}
                  />
                )
              )}

              <div className="flex justify-center items-center space-x-4 mt-6">
                <button
                  className="rounded-full w-12 h-12 flex items-center justify-center bg-[var(--accent)] text-[var(--accent-foreground)] hover:opacity-90 transition-colors"
                  onClick={togglePlayState}
                  disabled={loading || !audioUrl || Boolean(audioError)}
                >
                  <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                </button>

                <button
                  className="rounded-full w-12 h-12 flex items-center justify-center bg-[var(--accent)] text-[var(--accent-foreground)] hover:opacity-90 transition-colors ml-4"
                  onClick={() =>
                    selectedConversation && handleDownload(selectedConversation)
                  }
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Conversation"
        message="Are you sure you want to delete this conversation? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConversation}
        onCancel={() => setShowDeleteModal(false)}
        isDestructive={true}
      />
    </div>
  );
};

export default Conversations;
